export type AppEnv = 'local' | 'dev' | 'staging' | 'prod'

/**
 * Which environment the app is being served from, detected from the hostname on the client.
 *
 * The fallback is `dev`, deliberately: only the canonical production host counts as `prod`, so a
 * preview URL, a raw *.run.app hostname or an unrecognised domain gets badged rather than silently
 * passing for production. Getting that wrong in the safe direction means an extra badge; getting it
 * wrong the other way means a non-prod surface that looks like prod.
 *
 * `staging` has to be matched EXPLICITLY for that reason — before this existed, staging fell through
 * to the `dev` fallback and was badged "dev" while loading dev's favicon, which is exactly the
 * confusion the badge is meant to prevent.
 *
 * During SSR we return `prod` (no badge) and let the client correct it after mount.
 */
export function detectAppEnv(): AppEnv {
  if (import.meta.server || typeof window === 'undefined') return 'prod'
  const host = window.location.hostname
  if (host === 'localhost' || host === '127.0.0.1' || host.endsWith('.local')) return 'local'
  if (host === 'holamarcus.com' || host === 'www.holamarcus.com') return 'prod'
  if (host === 'staging.holamarcus.com' || host.startsWith('staging.')) return 'staging'
  return 'dev'
}

/**
 * Reactive form of {@link detectAppEnv}. Starts as `prod` (SSR-safe) and resolves on mount, so
 * wrap any env-badge UI in `<ClientOnly>` to avoid a hydration mismatch.
 */
export function useAppEnv() {
  const env = ref<AppEnv>('prod')
  onMounted(() => {
    env.value = detectAppEnv()
  })
  return env
}
